/* eslint-disable unicorn/filename-case */
import { useCallback, useEffect, useState } from "react";

import { generationModes } from "shared/constants";
import { noop, sleep } from "shared/functions";
import { useGlobalsContext } from "shared/hooks";
import { getIpLookup } from "shared/ipLookup";
import { AppConfiguration, GenerationMode } from "shared/types";
import { removePeriodsAfterURL } from "shared/utils/sourceUtils";

import { artificialWordByWordDelay } from "./constants";
import { parseSseErrorMessage } from "./functions";

const integrationFields = ["integration", "integrationData"];

function useConnectSSE(
  botId: string,
  config: AppConfiguration,
  supportEmail: string | null,
  chatId: string,
  setGenerationMode: React.Dispatch<React.SetStateAction<GenerationMode>>,
  setChatData: React.Dispatch<React.SetStateAction<any[]>>,
  setMessage: React.Dispatch<React.SetStateAction<string>>,
  setEventSource: React.Dispatch<React.SetStateAction<EventSource | null>>,
) {
  const { getParentUrl } = useGlobalsContext();
  const [ipAddress, setIpAddress] = useState<string>();
  useEffect(() => {
    getIpLookup({})
      .then(data => setIpAddress(data?.ip))
      .catch(noop);
  }, []);

  const onErrorSSE = useCallback(
    (sse: EventSource) => {
      sse.close();
      setGenerationMode(null);
    },
    [setGenerationMode],
  );

  const updateListener = useCallback(
    ({ data }: { data: string }) => {
      setChatData(prevChatData => {
        const chatArray = [...prevChatData];
        const lastMessage = chatArray.pop();
        if (lastMessage?.sent) {
          chatArray.push(lastMessage, {
            message: data,
            sent: false,
          });
        } else {
          chatArray.push({
            ...lastMessage,
            message: lastMessage?.message + data,
            sent: false,
          });
        }
        return chatArray;
      });
    },
    [setChatData],
  );

  const connectSSE = useCallback(
    (
      msg: string,
      chatUserId?: string,
      questionId?: string,
      is_support_request?: boolean,
      integration_type?: string,
      integration_data?: { [key: string]: any },
    ): Promise<EventSource> => {
      return new Promise(async (resolve, reject) => {
        if (!botId) {
          return;
        }

        setGenerationMode(generationModes.submitting);

        const parentUrlHref = await new Promise(_resolve =>
          getParentUrl(parentUrl =>
            _resolve(parentUrl ? parentUrl.href : window.location.href),
          ),
        );

        const params: {
          [key: string]: any;
        } = {
          data: JSON.stringify({
            input_text: msg,
            token: config.token,
            chat_id: chatId,
            ip_address: ipAddress,
            starter_question_id: questionId,
            chat_user_id: chatUserId,
            BaseOrigin: config.baseOrigin,
            page: parentUrlHref,
            is_support_request,
            integration_type,
            integration_data,
          }),
        };

        for (const key of Object.keys(params)) {
          if (!params[key] || typeof params[key] !== "string") {
            delete params[key];
          }
        }

        try {
          const sse = new EventSource(
            `${
              config.serviceBaseUrl
            }/v1/botsonic/generate/sse?${new URLSearchParams(
              params,
            ).toString()}`,
          );

          setChatData(prevChatData => {
            const chatArray = [...prevChatData];
            chatArray.push({
              message: msg,
              sent: true,
            });
            return chatArray;
          });

          sse?.addEventListener("message_id", function (event) {
            setChatData(prevChatData => {
              const chatArray = [...prevChatData];
              const lastMessage = chatArray.pop();
              if (lastMessage?.sent) {
                chatArray.push(lastMessage, {
                  id: event.data,
                  message: lastMessage?.message,
                  sent: false,
                });
              } else {
                chatArray.push({
                  ...lastMessage,
                  id: event.data,
                });
              }
              return chatArray;
            });
          });

          const processStarterQuestion = async (answer: string) => {
            setGenerationMode(generationModes.generating);

            const fakeEventSource = {
              open: true,
              close() {
                this.open = false;
              },
            };
            setEventSource(fakeEventSource as unknown as EventSource);

            const answerWords: string[] = answer.split(" ");
            const sendWord = async () => {
              const newWord = answerWords.shift();
              updateListener({
                data: `${newWord}${answerWords.length ? " " : ""}`,
              });
              return sleep(artificialWordByWordDelay);
            };

            while (answerWords.length && fakeEventSource.open) {
              await sendWord();
            }

            setGenerationMode(null);
            setEventSource(null);
          };

          sse?.addEventListener("update", (event: MessageEvent<any>) => {
            if (questionId) {
              return processStarterQuestion(event.data);
            }
            updateListener(event);
          });

          sse?.addEventListener("end", function () {
            setChatData(prevChatData => {
              const chatArray = [...prevChatData];
              const lastMessage = chatArray.pop();

              chatArray.push({
                ...lastMessage,
                message: removePeriodsAfterURL(lastMessage?.message),
                sent: false,
              });
              return chatArray;
            });

            setMessage("");
            setGenerationMode(null);

            sse.close();
          });

          sse?.addEventListener("sources", function (event) {
            setChatData(prevChatData => {
              const chatArray = [...prevChatData];
              const lastMessage = chatArray.pop();
              let sources = [];
              try {
                sources = JSON.parse(event.data);
              } catch {
                sources = [];
              }
              chatArray.push({
                ...lastMessage,
                sent: false,
                sources,
              });
              return chatArray;
            });
          });

          const getIntegrationListener =
            (key: (typeof integrationFields)[number]) =>
            (event: MessageEvent<any>) => {
              setChatData(prevChatData => {
                const chatArray = [...prevChatData];
                const lastMessage = chatArray.pop();
                chatArray.push({
                  ...lastMessage,
                  message: removePeriodsAfterURL(lastMessage?.message),
                  sent: false,
                  [key]: JSON.parse(event.data),
                });
                return chatArray;
              });
              setMessage("");
              setGenerationMode(null);
            };

          for (const integrationField of integrationFields) {
            sse?.addEventListener(
              integrationField,
              getIntegrationListener(integrationField),
            );
          }
          sse?.addEventListener("error", function (event: any) {
            setChatData(prevChatData => {
              const chatArray = [...prevChatData];
              const message =
                parseSseErrorMessage(event.data, config.baseOrigin) ||
                "Sorry, our AI is recharging at the moment. " +
                  `Please get in touch with the humans${
                    supportEmail ? " at " + supportEmail : ""
                  }.`;

              chatArray.push({
                message,
                sent: false,
              });
              return chatArray;
            });

            setGenerationMode(null);
            sse.close();
          });

          sse.addEventListener("error", () => onErrorSSE(sse));
          sse.addEventListener("open", () => resolve(sse));
        } catch (err) {
          reject(err);
        }
      });
    },
    [
      botId,
      setGenerationMode,
      config.token,
      config.baseOrigin,
      config.serviceBaseUrl,
      chatId,
      ipAddress,
      getParentUrl,
      setChatData,
      setEventSource,
      updateListener,
      setMessage,
      supportEmail,
      onErrorSSE,
    ],
  );

  return {
    connectSSE,
  };
}

export default useConnectSSE;
